import type { FunctionComponent } from 'react'

import { useEffect, useMemo } from 'react'

import { useFormContext } from './FormContext'

export type FieldFlagHocProps<P = {}> = {
  name: string
  flag: string | string[]
  erroring?: boolean
} & P

export type InternalFieldFlagHocProps<P = {}> = {
  isActive: boolean
  activeFlags: string[]
} & P

export type FieldFlagHocOptions = {
  erroring?: boolean
}

export const fieldFlagHoc = <P extends Record<string, any>>(
  options: FieldFlagHocOptions,
  FieldFlag: FunctionComponent<InternalFieldFlagHocProps<P>>,
) => {
  const { erroring: forceErroring } = options

  const FieldFlagHoc = (props: FieldFlagHocProps<P>) => {
    const { name, flag, erroring } = props

    const flags = useMemo(() => (typeof flag === 'string' ? [flag] : flag), [flag])

    const [formState, dispatch] = useFormContext()
    if (!formState.belongsToForm) console.warn('Field flags only work within a <Form>')

    const showErrorFlagsAfterSubmit = formState.showErrorFlagsAfterSubmit
    const hasAttemptedSubmit = formState.hasAttemptedSubmit
    const isErroring = forceErroring ?? erroring

    const canBeActive = hasAttemptedSubmit || !showErrorFlagsAfterSubmit
    // formFlags/fieldFlags = validators; providedFlags = Form `flags` prop (e.g. API issues)
    const activeFlagsMemo = () =>
      canBeActive
        ? flags.filter(
            (f) =>
              formState.formFlags[name]?.has(f) ||
              formState.fieldFlags[name]?.has(f) ||
              formState.providedFlags[name]?.includes(f),
          )
        : []
    const activeFlags = useMemo(activeFlagsMemo, [
      flags,
      formState.fieldFlags,
      formState.formFlags,
      formState.providedFlags,
      name,
      canBeActive,
    ])
    const isActive = activeFlags.length !== 0

    const setErroringFlagsEffect = () => {
      if (!isErroring) return
      dispatch({ type: 'setErroringFieldFlags', name, flags })
      return () => {
        dispatch({ type: 'unsetErroringFieldFlags', name, flags })
      }
    }
    useEffect(setErroringFlagsEffect, [dispatch, name, flags, isErroring])

    return <FieldFlag {...props} isActive={isActive} activeFlags={activeFlags} />
  }

  return FieldFlagHoc
}
